import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import {
  gcsUploadFile,
  gcsDeleteFile,
  gcsGetSignedReadUrl,
  gcsGetSignedDownloadUrl,
  gcsCopyFile,
  gcsGetPublicUrl,
  detectUrlType,
  extractGcsPath,
} from '@/lib/gcsStorage'

const GCS_BUCKET_URL = 'https://storage.googleapis.com/trainingmatrix'

/**
 * React hook for GCS file operations with progress tracking and backward compatibility.
 */
export function useGcsUpload() {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  /**
   * Upload a file with XMLHttpRequest progress tracking.
   * Returns { publicUrl, filePath }
   */
  const uploadFileWithProgress = useCallback(async (file, { folderPath, fileName, contentType }) => {
    setUploading(true)
    setUploadProgress(0)

    try {
      const safeName = fileName || file.name
      const safeContentType = contentType || file.type || 'application/octet-stream'

      // Get signed upload URL
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: safeName,
            contentType: safeContentType,
            folderPath,
          },
        }
      )

      if (urlError || !urlData?.signedUrl) {
        throw new Error(urlError?.message || 'Failed to get upload URL')
      }

      // Upload with XHR for progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) resolve()
          else reject(new Error(`Upload failed with status ${xhr.status}`))
        })

        xhr.addEventListener('error', () => reject(new Error('Upload failed')))
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')))

        xhr.open('PUT', urlData.signedUrl)
        xhr.setRequestHeader('Content-Type', safeContentType)
        xhr.send(file)
      })

      return {
        publicUrl: urlData.publicUrl,
        filePath: urlData.fileName,
      }
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }, [])

  const uploadFile = useCallback(async (file, opts) => {
    return gcsUploadFile(file, opts)
  }, [])

  const deleteFile = useCallback(async (filePath) => {
    return gcsDeleteFile(filePath)
  }, [])

  const getSignedReadUrl = useCallback(async (filePath) => {
    return gcsGetSignedReadUrl(filePath)
  }, [])

  const getSignedDownloadUrl = useCallback(async (filePath) => {
    return gcsGetSignedDownloadUrl(filePath)
  }, [])

  const copyFile = useCallback(async (sourcePath, destinationPath) => {
    return gcsCopyFile(sourcePath, destinationPath)
  }, [])

  const getPublicUrl = useCallback((filePath) => {
    return gcsGetPublicUrl(filePath)
  }, [])

  /**
   * Backward-compatible URL resolver.
   * Handles all URL formats stored in the DB:
   * - Full GCS URLs → return as-is
   * - Full Supabase URLs → return as-is (still served by Supabase)
   * - Relative paths (legacy Supabase) → try Supabase signed URL
   * - GCS file paths → generate signed read URL
   */
  const getViewableUrl = useCallback(async (urlOrPath, supabaseBucket = 'certificates') => {
    if (!urlOrPath) return null

    const type = detectUrlType(urlOrPath)

    if (type === 'gcs') {
      // Full GCS URL — extract path and get signed read URL for private files
      const path = extractGcsPath(urlOrPath)
      try {
        return await gcsGetSignedReadUrl(path)
      } catch {
        // Fallback to public URL if signed fails
        return urlOrPath
      }
    }

    if (type === 'supabase') {
      // Full Supabase URL — still served by Supabase, return as-is
      return urlOrPath
    }

    if (type === 'path') {
      // Relative path — could be old Supabase or new GCS path
      // Check if it starts with a known GCS folder prefix
      const gcsPrefix = ['certificates/', 'documents/', 'project-files/', 'chat-attachments/', 'avatars/', 'trainingmatrix/', 'uploads/']
      const isGcsPath = gcsPrefix.some(prefix => urlOrPath.startsWith(prefix))

      if (isGcsPath) {
        try {
          return await gcsGetSignedReadUrl(urlOrPath)
        } catch {
          return gcsGetPublicUrl(urlOrPath)
        }
      }

      // Legacy Supabase relative path — use Supabase signed URL
      try {
        const { data, error } = await supabase.storage
          .from(supabaseBucket)
          .createSignedUrl(urlOrPath, 3600)
        if (!error && data?.signedUrl) return data.signedUrl
      } catch {
        // ignore
      }

      // Fallback: try as GCS path with bucket prefix
      try {
        return await gcsGetSignedReadUrl(`${supabaseBucket}/${urlOrPath}`)
      } catch {
        return null
      }
    }

    // 'other' — return as-is
    return urlOrPath
  }, [])

  /**
   * Get a downloadable URL (forces browser download).
   * Handles both GCS and legacy Supabase paths.
   */
  const getDownloadableUrl = useCallback(async (urlOrPath, supabaseBucket = 'certificates') => {
    if (!urlOrPath) return null

    const type = detectUrlType(urlOrPath)

    if (type === 'gcs') {
      const path = extractGcsPath(urlOrPath)
      return gcsGetSignedDownloadUrl(path)
    }

    if (type === 'supabase') {
      // For full Supabase URLs, just return — browser can download directly
      return { signedUrl: urlOrPath, fileName: urlOrPath.split('/').pop() }
    }

    if (type === 'path') {
      const gcsPrefix = ['certificates/', 'documents/', 'project-files/', 'chat-attachments/', 'avatars/', 'trainingmatrix/', 'uploads/']
      const isGcsPath = gcsPrefix.some(prefix => urlOrPath.startsWith(prefix))

      if (isGcsPath) {
        return gcsGetSignedDownloadUrl(urlOrPath)
      }

      // Legacy Supabase path — download via Supabase
      const { data, error } = await supabase.storage
        .from(supabaseBucket)
        .download(urlOrPath)
      if (!error && data) {
        const url = URL.createObjectURL(data)
        return { signedUrl: url, fileName: urlOrPath.split('/').pop() }
      }

      // Fallback to GCS
      return gcsGetSignedDownloadUrl(`${supabaseBucket}/${urlOrPath}`)
    }

    return { signedUrl: urlOrPath, fileName: urlOrPath.split('/').pop() }
  }, [])

  return {
    uploading,
    uploadProgress,
    uploadFile,
    uploadFileWithProgress,
    deleteFile,
    getSignedReadUrl,
    getSignedDownloadUrl,
    copyFile,
    getPublicUrl,
    getViewableUrl,
    getDownloadableUrl,
    detectUrlType,
    extractGcsPath,
  }
}
